import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import { type TRelationType, createEmptyRelationType } from '@timeedit/types';
import api from '../services/api.service';
import type { TAppDispatch } from './store';

type TRelationTypesState = IDefaultReducerState & {
  allRelationTypes: TRelationType[];
  map: Record<number, TRelationType>;
  firstRelationType: TRelationType | null;
  secondRelationType: TRelationType | null;
};

export const initialState: TRelationTypesState = {
  loading: false,
  hasErrors: false,
  allRelationTypes: [],
  map: {},
  firstRelationType: null,
  secondRelationType: null,
};

// Slice
const slice = createSlice({
  name: 'relationTypes',
  initialState,
  reducers: {
    fetchRelationTypesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchRelationTypesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TRelationType>>
    ) => {
      state.allRelationTypes = payload.results;
      state.map = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchRelationTypesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createRelationTypeSuccess: (
      state,
      { payload }: PayloadAction<TRelationType>
    ) => {
      if (state.allRelationTypes[0]?.id === 0) return;
      state.allRelationTypes.unshift(payload);
      state.firstRelationType = payload;
      state.map[payload.id] = payload;
    },
    deleteRelationTypeRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteRelationTypeSuccess: (
      state,
      { payload: relationTypeId }: PayloadAction<number>
    ) => {
      state.allRelationTypes = state.allRelationTypes.filter(
        (relationType: TRelationType) => relationType.id !== relationTypeId
      );
      state.map[relationTypeId] = undefined;
      if (state.firstRelationType?.id === relationTypeId) {
        state.firstRelationType = null;
      }
      if (state.secondRelationType?.id === relationTypeId) {
        state.secondRelationType = null;
      }
      finishedLoadingSuccess(state);
    },
    deleteRelationTypeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateRelationTypeRequest: (state) => {
      isLoadingRequest(state);
    },
    updateRelationTypeSuccess: (
      state,
      { payload }: PayloadAction<TRelationType>
    ) => {
      const index = state.allRelationTypes.findIndex(
        (relationType: TRelationType) => relationType.id === payload.id
      );
      state.allRelationTypes[index] = payload;
      state.map[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    closeRelationType: (
      state,
      { payload: relationTypeId }: PayloadAction<number>
    ) => {
      if (state.firstRelationType?.id === relationTypeId) {
        state.firstRelationType = null;
      }
      if (state.secondRelationType?.id === relationTypeId) {
        state.secondRelationType = null;
      }
    },
    updateRelationTypeFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstRelationType: (
      state,
      { payload }: PayloadAction<TRelationType>
    ) => {
      const prevRelationType = state.firstRelationType;
      state.firstRelationType = payload;
      if (prevRelationType?.id === 0) {
        state.allRelationTypes = state.allRelationTypes.filter(
          (relationType: TRelationType) => relationType.id !== 0
        );
        state.map[0] = undefined;
      }
    },
    setSecondRelationType: (
      state,
      { payload }: PayloadAction<TRelationType>
    ) => {
      state.secondRelationType = payload;
    },
    changeRelationType: <Key extends keyof TRelationType>(
      state: TRelationTypesState,
      {
        payload,
      }: PayloadAction<{
        isFirstRelationType: boolean;
        property: Key;
        value: TRelationType[Key];
      }>
    ) => {
      const { isFirstRelationType, property, value } = payload;
      if (isFirstRelationType) {
        state.firstRelationType[property] = value;
      } else {
        state.secondRelationType[property] = value;
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const relationTypesLoading = (state: TRootState) =>
  state.relationTypes.loading;
export const relationTypesSelector = (state: TRootState) =>
  state.relationTypes.allRelationTypes;
export const relationTypeMapSelector = (state: TRootState) =>
  state.relationTypes.map;

export const selectedRelationTypeIdsSelector = createSelector(
  [
    (state: TRootState) => state.relationTypes.firstRelationType,
    (state: TRootState) => state.relationTypes.secondRelationType,
  ],
  (firstRelationType, secondRelationType) => {
    return _.compact([firstRelationType, secondRelationType]).map(
      (relationType: TRelationType) => relationType.id
    );
  }
);

export const firstRelationTypeSelector = (state: TRootState) => {
  return state.relationTypes.firstRelationType;
};
export const secondRelationTypeSelector = (state: TRootState) => {
  return state.relationTypes.secondRelationType;
};

// Actions
export const {
  fetchRelationTypesRequest,
  fetchRelationTypesSuccess,
  fetchRelationTypesFailure,
  createRelationTypeSuccess,
  deleteRelationTypeRequest,
  deleteRelationTypeSuccess,
  deleteRelationTypeFailure,
  updateRelationTypeRequest,
  updateRelationTypeSuccess,
  updateRelationTypeFailure,
  setFirstRelationType,
  setSecondRelationType,
  changeRelationType,
  closeRelationType,
} = slice.actions;

export const fetchRelationTypes = createAppAsyncThunk(
  'relationTypes/fetchRelationTypes',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchRelationTypesRequest());
      const relationTypes = await api.get({ endpoint: `/relation-types` });
      dispatch(fetchRelationTypesSuccess(relationTypes));
    } catch (e) {
      dispatch(fetchRelationTypesFailure());
      return console.error(e);
    }
  }
);

export const createUnsavedRelationType = (): TAppThunk => async (dispatch) => {
  const response = createEmptyRelationType();
  dispatch(createRelationTypeSuccess(response));
};

export const copyRelationType =
  (relationType: TRelationType): TAppThunk =>
  (dispatch) => {
    const response = {
      ...relationType,
      id: 0,
      name: `${relationType.name} (copy)`,
    };
    dispatch(createRelationTypeSuccess(response));
  };

export const deleteRelationType = createAppAsyncThunk<void, number>(
  'relationTypes/deleteRelationType',
  async (relationTypeId, { dispatch }) => {
    try {
      dispatch(deleteRelationTypeRequest());
      await api.delete({
        endpoint: `/relation-types/${relationTypeId}`,
      });
      dispatch(deleteRelationTypeSuccess(relationTypeId));
    } catch (e) {
      dispatch(deleteRelationTypeFailure());
      return console.error(e);
    }
  }
);

export const upsertRelationType = createAppAsyncThunk<void, TRelationType>(
  'relationTypes/upsertRelationType',
  async (relationType, { dispatch }) => {
    try {
      dispatch(updateRelationTypeRequest());
      const { id, ...relationTypeBody } = relationType;
      if (id !== 0) {
        const response = await api.patch({
          endpoint: `/relation-types/${id}`,
          data: relationTypeBody,
        });
        dispatch(updateRelationTypeSuccess(response));
      } else {
        await api.post({
          endpoint: `/relation-types`,
          data: relationTypeBody,
        });
        dispatch(fetchRelationTypes());
      }
      dispatch(closeRelationType(relationType.id));
    } catch (e: any) {
      dispatch(updateRelationTypeFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  }
);

export const checkRelationTypeSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_: TAppDispatch, getState) => {
    const { relationTypes } = getState();
    const {
      firstRelationType,
      secondRelationType,
      map: relationTypeMapping,
    } = relationTypes;

    if (isFirstDrawer && firstRelationType) {
      return checkSavedDisabled(
        firstRelationType,
        relationTypeMapping[firstRelationType.id]
      );
    }
    if (!isFirstDrawer && secondRelationType) {
      return checkSavedDisabled(
        secondRelationType,
        relationTypeMapping[secondRelationType.id]
      );
    }
    return true;
  };

export const checkIfFirstRelationTypeExists =
  (): TAppThunk<boolean> => (_: TAppDispatch, getState) =>
    !!getState().relationTypes.firstRelationType;
